import type { QueryKey } from "@tanstack/react-query";
import { queryKeys } from "./keys";

/**
 * Domain entity types that participate in cache invalidation.
 */
export const EntityType = {
	INVESTMENT: "investment",
	BENEFICIARY: "beneficiary",
	INVESTOR: "investor",
	PROJECT: "project",
	CONTRACT: "contract",
	CLASSIFICATION: "classification",
	TEMPLATE: "template",
	ORGANIZATION: "organization",
	NOTIFICATION: "notification",
	DASHBOARD: "dashboard",
	PIPELINE: "pipeline",
} as const;

export type EntityType = (typeof EntityType)[keyof typeof EntityType];

/**
 * Each entity's own query key prefixes.
 * Used by resolveInvalidationKeys to map entities to TanStack Query keys.
 *
 * When adding a new query to an entity (e.g. beneficiaries.analytics), add it
 * HERE — every mutation that affects that entity will auto-invalidate it.
 */
const ENTITY_QUERY_KEYS: Record<EntityType, (orgId: string) => QueryKey[]> = {
	investment: (orgId) => [queryKeys.investments.all(orgId)],
	beneficiary: (orgId) => [
		queryKeys.beneficiaries.all(orgId),
		queryKeys.beneficiaryDropdown.all(orgId),
	],
	investor: (orgId) => [
		queryKeys.investors.all(orgId),
		queryKeys.investorDropdown.all(orgId),
	],
	project: (orgId) => [queryKeys.projects.all(orgId)],
	contract: (orgId) => [queryKeys.contracts.all(orgId)],
	classification: () => [],
	template: (orgId) => [queryKeys.templates.all(orgId)],
	organization: (orgId) => [queryKeys.organization.all(orgId)],
	notification: (orgId) => [queryKeys.notifications.all(orgId)],
	dashboard: (orgId) => [queryKeys.dashboard.all(orgId)],
	pipeline: (orgId) => [queryKeys.pipeline.all(orgId)],
};

/**
 * Entity dependency graph — single source of truth for cross-entity cache relationships.
 *
 * When entity X is mutated, all listed entities also become stale.
 * Resolved recursively with cycle protection by resolveInvalidationKeys.
 *
 * Read as: "When [key] changes, [values] also need to refresh."
 *
 * When adding a new cross-entity relationship (e.g. a new widget that shows
 * beneficiary data derived from investments), add it HERE — all mutations that
 * affect the source entity will auto-invalidate the dependent.
 */
const ENTITY_DEPENDENTS: Record<EntityType, EntityType[]> = {
	investment: ["beneficiary", "investor", "dashboard", "pipeline", "contract"],
	beneficiary: ["dashboard", "pipeline"],
	investor: ["dashboard"],
	project: ["dashboard"],
	contract: ["investment", "dashboard"],
	classification: ["beneficiary"],
	template: [],
	organization: [],
	notification: [],
	dashboard: [],
	pipeline: [],
};

/**
 * Resolves entity types into the full set of query keys that need invalidation.
 *
 * Traverses the dependency graph recursively with cycle protection.
 * For each entity (source + dependents), collects its query key prefixes.
 *
 * Example: resolveInvalidationKeys(["investment"], orgId) returns keys for
 * investments, beneficiaries, beneficiaryDropdown, investors, investorDropdown,
 * dashboard, pipeline, and contracts.
 */
export function resolveInvalidationKeys(
	entities: EntityType[],
	orgId: string,
): QueryKey[] {
	const visited = new Set<EntityType>();
	const keys: QueryKey[] = [];

	function collect(entity: EntityType) {
		if (visited.has(entity)) return;
		visited.add(entity);
		for (const key of ENTITY_QUERY_KEYS[entity](orgId)) {
			keys.push(key);
		}
		for (const dep of ENTITY_DEPENDENTS[entity]) {
			collect(dep);
		}
	}

	for (const entity of entities) {
		collect(entity);
	}
	return keys;
}
